import api from '../config/axios';

export interface DeepSeekMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Talks to the backend's /api/ai/chat proxy instead of calling an LLM
 * directly from the browser. The backend dispatches to whichever provider
 * the organization has configured (a BYOK key, e.g. Anthropic/OpenAI) or
 * falls back to the self-hosted Ollama instance when none is set — see
 * AgriVision's services/ai_service.go. This used to call
 * http://localhost:11434 directly, which only ever worked on a developer's
 * own machine and never in production.
 */
class DeepSeekService {
  async chat(messages: Array<{ role: string; content: string }>, context?: string): Promise<string> {
    // The last message is the user's new message; everything before it is
    // history. The backend expects them separately.
    const last = messages[messages.length - 1];
    const history = messages.slice(0, -1).filter((m) => m.role !== 'system');

    const response = await api.post('/ai/chat', {
      message: last?.content ?? '',
      history: history.map((m) => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content,
      })),
      context,
    });

    const reply = response.data?.reply;
    if (!reply) {
      throw new Error('No response from AI service');
    }
    return reply;
  }

  async quickChat(message: string, context?: string): Promise<string> {
    return this.chat([{ role: 'user', content: message }], context);
  }
}

export const deepSeekService = new DeepSeekService();
